import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import { isIOS } from 'react-device-detect';
import { VideoPropertiesService } from '../../services/videoProperties/VideoPropertiesService';
import { getIosFrameRotation } from '../../utils/getIosFrameRotation';
import { getIosPreviewRotation } from '../../utils/getIosPreviewRotation';
import { isUserFacingVideoTrack } from '../../utils/media';
import { generateThumbnail } from '../../utils/thumbnail';
import { AudioProducer } from './AudioProducer';
import { MediaPacketizer } from './MediaPacketizer';
import { VideoProducer } from './VideoProducer';
export class MediaProducer extends EventEmitter {
    videoProducer;
    audioProducer;
    mediaPacketizer;
    videoDataCounter = 0;
    audioDataCounter = 0;
    currentMediaStreamId;
    screenSharing = false;
    videoEncodingQuality = 'sd';
    videoProperties;
    lastVideoFrameTimestampMicros = 0;
    lastAudioFrameTimestampMicros = 0;
    lastAudioFrameArrivalTimeMicros = 0;
    // Track IDs for sending EndedTrack messages
    currentVideoTrackId;
    currentAudioTrackId;
    constructor() {
        super();
        this.videoProducer = new VideoProducer();
        this.videoProducer.on('encodedVideoData', (videoData) => {
            this.onEncodedVideoData(videoData);
        });
        this.videoProducer.on('videoFrame', (videoFrame) => {
            this.emit('videoFrame', videoFrame);
        });
        this.audioProducer = new AudioProducer();
        this.audioProducer.on('audioData', (audioData) => {
            this.onEncodedAudioData(audioData);
        });
        this.audioProducer.on('audioVolume', (volume) => {
            this.emit('audioVolume', volume);
        });
        this.mediaPacketizer = new MediaPacketizer();
        this.mediaPacketizer.on('mediaPacketFragment', (fragment) => {
            this.emit('mediaPacketFragment', fragment);
        });
    }
    async start(stream) {
        console.log('MediaProducer::start(), stream', stream.id, stream.getTracks());
        if (this.currentMediaStreamId !== undefined) {
            console.log('mediaStreamId changed', this.currentMediaStreamId);
        }
        await this.changeVideoTrack(stream.getVideoTracks()[0]);
        await this.changeAudioTrack(stream.getAudioTracks()[0]);
    }
    async changeVideoTrack(videoTrack) {
        // Always stop the current encoder/extractor before switching tracks to
        // avoid feeding frames from multiple sources concurrently.
        await this.videoProducer.stop();
        if (videoTrack) {
            this.currentVideoTrackId = videoTrack.id;
            this.mediaPacketizer.setIsVideoActive(true);
            this.screenSharing = !!videoTrack.isScreenShare;
            // Reset timestamp synchronization when switching video tracks
            this.lastVideoFrameTimestampMicros = 0;
            this.lastAudioFrameTimestampMicros = 0;
            this.lastAudioFrameArrivalTimeMicros = 0;
            // Reset video frame counter for new track
            this.videoDataCounter = 0;
            await this.videoProducer.start(videoTrack);
        }
        else {
            this.currentVideoTrackId = undefined;
            this.mediaPacketizer.setIsVideoActive(false);
        }
    }
    async changeAudioTrack(audioTrack) {
        await this.audioProducer.stop();
        if (audioTrack) {
            this.currentAudioTrackId = audioTrack.id;
            const settings = audioTrack.getSettings();
            console.log('Audio track sample rate:', settings.sampleRate);
            // Reset audio frame counter for new track
            this.audioDataCounter = 0;
            await this.audioProducer.start(audioTrack);
        }
        else {
            this.currentAudioTrackId = undefined;
        }
    }
    startBroadcasting() {
        console.log('MediaProducer::startBroadcasting()');
        this.videoProducer.startBroadcasting();
        this.audioProducer.startBroadcasting();
    }
    stopBroadcasting() {
        console.log('MediaProducer::stopBroadcasting()');
        this.videoProducer.stopBroadcasting();
        this.audioProducer.stopBroadcasting();
    }
    async stop() {
        console.log('MediaProducer::stop()');
        await this.videoProducer.stop();
        await this.audioProducer.stop();
        this.lastVideoFrameTimestampMicros = 0;
        this.lastAudioFrameTimestampMicros = 0;
        this.lastAudioFrameArrivalTimeMicros = 0;
    }
    feedVideoFrame(videoFrame) {
        this.updateVideoProperties(videoFrame);
        this.videoProducer.feedVideoFrame(videoFrame);
    }
    onEncodedVideoData(videoData) {
        const currentTimeMicros = Math.round((performance.now() + performance.timeOrigin) * 1000);
        let videoTimestampMicros = Number(videoData.timestamp);
        if (this.lastAudioFrameArrivalTimeMicros !== 0) {
            const realTimeDiff = currentTimeMicros - this.lastAudioFrameArrivalTimeMicros;
            videoTimestampMicros =
                realTimeDiff + this.lastAudioFrameTimestampMicros;
        }
        const finalTimestamp = BigInt(videoTimestampMicros);
        this.lastVideoFrameTimestampMicros = videoTimestampMicros;
        if (this.videoDataCounter % 100 === 0) {
            const timeDiff = this.lastAudioFrameTimestampMicros -
                this.lastVideoFrameTimestampMicros;
            console.log('Audio is ahead of video by ', Math.round(timeDiff / 1000), 'ms');
        }
        const videoFrame = {
            frameNumber: BigInt(this.videoDataCounter++),
            timestamp: finalTimestamp,
            duration: videoData.duration,
            isKeyFrame: videoData.isKeyFrame,
            data: videoData.data,
            extradata: videoData.extradata,
            codedWidth: videoData.codedWidth,
            codedHeight: videoData.codedHeight,
            codec: videoData.codec,
            isScreenSharing: this.screenSharing,
            rotation: this.calculateRotation(),
            trackId: this.videoProducer.getTrack().id,
            done: false,
        };
        this.mediaPacketizer.addVideoFrame(videoFrame, this.currentMediaStreamId ?? '');
    }
    onEncodedAudioData(audioData) {
        this.audioDataCounter++;
        this.lastAudioFrameArrivalTimeMicros = Math.round((performance.now() + performance.timeOrigin) * 1000);
        const finalTimestamp = audioData.timestamp;
        this.lastAudioFrameTimestampMicros = Number(audioData.timestamp);
        const audioFrame = {
            data: audioData.data,
            timestamp: finalTimestamp,
            duration: BigInt(audioData.duration),
            frameNumber: BigInt(this.audioDataCounter),
            isKeyFrame: audioData.isKeyFrame,
            extradata: audioData.extradata,
            trackId: this.currentAudioTrackId ?? '',
            done: false,
        };
        this.mediaPacketizer.addAudioFrames([audioFrame], this.currentMediaStreamId ?? '');
    }
    setVideoEncodingQuality(videoEncodingQuality) {
        this.videoEncodingQuality = videoEncodingQuality;
        let bitrate = Settings.videoEncoderBitrateSd;
        if (videoEncodingQuality === 'hd') {
            bitrate = Settings.videoEncoderBitrateHd;
        }
        else if (videoEncodingQuality === 'uhd') {
            bitrate = Settings.videoEncoderBitrateUhd;
        }
        this.videoProducer.setVideoEncodingBitrate(bitrate);
    }
    getVideoEncodingQuality() {
        return this.videoEncodingQuality;
    }
    /**
     * Generates a thumbnail from the current video track
     * @returns A promise that resolves to a Blob containing the thumbnail image
     */
    async generateThumbnail() {
        const videoTrack = this.videoProducer.getTrack();
        return generateThumbnail(videoTrack);
    }
    /**
     * Compute the clockwise rotation (in degrees) needed to render a captured
     * camera frame upright relative to the current device orientation.
     *
     * - Returns 0 on platforms that don’t require adjustments (non-iOS).
     * - Considers natural vs current orientation and front vs rear lens.
     *
     * @returns Clockwise rotation in degrees.
     */
    calculateRotation() {
        // Non-iOS platforms don't require rotation correction.
        if (!isIOS) {
            return 0;
        }
        const isUserFacingCamera = isUserFacingVideoTrack(this.videoProducer.getTrack());
        return getIosFrameRotation(screen.orientation, isUserFacingCamera);
    }
    calculatePreviewRotation() {
        if (!isIOS) {
            return 0;
        }
        const isUserFacingCamera = isUserFacingVideoTrack(this.videoProducer.getTrack());
        return getIosPreviewRotation(screen.orientation, isUserFacingCamera);
    }
    updateVideoProperties(videoFrame) {
        const rotation = this.calculatePreviewRotation();
        const videoType = this.screenSharing ? 'screen' : 'camera';
        if (!this.videoProperties ||
            this.videoProperties.width !== videoFrame.codedWidth ||
            this.videoProperties.height !== videoFrame.codedHeight ||
            this.videoProperties.rotation !== rotation ||
            this.videoProperties.type !== videoType) {
            this.videoProperties = {
                height: videoFrame.codedHeight,
                width: videoFrame.codedWidth,
                rotation,
                type: videoType,
            };
            VideoPropertiesService.getNotificationInterface().notifyVideoPropertiesChanged(this.videoProperties);
        }
    }
}
